import React, { useState, useEffect } from 'react';
import Button from '../components/ui/Button';
import Autocomplete from '../components/ui/Autocomplete';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

interface User {
  id: number;
  name: string;
  username: string;
  email: string;
}

const UploadContactsCsv: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<User | null>(null);
  const [useCsvAgentIds, setUseCsvAgentIds] = useState(false);
  const [agentSearchValue, setAgentSearchValue] = useState('');
  const { hasPermission } = useAuth();

  const searchAgents = async (query: string): Promise<User[]> => {
    try {
      const response = await apiService.get<{ data: { items: User[] } }>('users', { search: query });
      return (response.data as any)?.items || [];
    } catch (error) {
      console.error('Error searching agents:', error);
      return [];
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check if file is CSV
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a valid CSV file');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    if (!useCsvAgentIds && !selectedAgent) {
      toast.error('Please select an agent or enable CSV agent assignment');
      return;
    }

    if (!hasPermission('upload-contacts-csv', 'add')) {
      toast.error('You do not have permission to upload contacts.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add agent_id parameter if not using CSV agent IDs
      if (!useCsvAgentIds && selectedAgent) {
        formData.append('agent_id', selectedAgent.id.toString());
      }

      console.log('Uploading file:', file.name);
      console.log('FormData contents:', {
        file: file.name,
        agent_id: !useCsvAgentIds && selectedAgent ? selectedAgent.id : 'using CSV agent IDs'
      });

      // Use uploadFileWithData method for proper FormData handling with additional parameters
      const response = await apiService.uploadFileWithData<{
        imported: number;
        errors: string[];
        total_rows: number;
      }>('contacts/upload-csv', formData, (progress) => {
        setUploadProgress(progress);
      });
      
      console.log('Upload response:', response);
      
      if (response.success) {
        const result = response.data;
        toast.success(`Successfully imported ${result.imported} contacts${result.errors.length > 0 ? ` with ${result.errors.length} errors` : ''}`);
        
        if (result.errors.length > 0) {
          console.log('Import errors:', result.errors);
          // Show errors in a more detailed way
          result.errors.forEach((error, index) => {
            if (index < 5) { // Show first 5 errors
              toast.warning(error);
            }
          });
          if (result.errors.length > 5) {
            toast.warning(`... and ${result.errors.length - 5} more errors. Check console for details.`);
          }
        }
        
        setFile(null);
        setUploadProgress(0);
        // Reset file input
        const fileInput = document.getElementById('csv-file') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        console.error('Upload failed:', response);
        toast.error(response.message || 'Failed to upload contacts');
      }
    } catch (error) {
      console.error('Error uploading contacts:', error);
      
      // More detailed error handling
      if (error instanceof Error) {
        toast.error(`Upload failed: ${error.message}`);
      } else {
        toast.error('Error uploading contacts. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const downloadTemplate = () => {
    // Create a sample CSV template with agent_id field
    const csvContent = useCsvAgentIds 
      ? `Name,Phone Number,Email,Company,Agent ID
John Doe,+1234567890,john.doe@example.com,ABC Company,1
Jane Smith,+0987654321,jane.smith@example.com,XYZ Corp,2
Mike Johnson,+1122334455,mike.johnson@example.com,Tech Solutions,3
Sarah Wilson,+1555666777,sarah.wilson@example.com,Global Corp,4
David Brown,+1888999000,david.brown@example.com,Startup Inc,5`
      : `Name,Phone Number,Email,Company
John Doe,+1234567890,john.doe@example.com,ABC Company
Jane Smith,+0987654321,jane.smith@example.com,XYZ Corp
Mike Johnson,+1122334455,mike.johnson@example.com,Tech Solutions
Sarah Wilson,+1555666777,sarah.wilson@example.com,Global Corp
David Brown,+1888999000,david.brown@example.com,Startup Inc`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = useCsvAgentIds ? 'contacts_template_with_agent_ids.csv' : 'contacts_template_single_agent.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadSingleAgentTemplate = () => {
    const csvContent = `Name,Phone Number,Email,Company
John Doe,+1234567890,john.doe@example.com,ABC Company
Jane Smith,+0987654321,jane.smith@example.com,XYZ Corp
Mike Johnson,+1122334455,mike.johnson@example.com,Tech Solutions
Sarah Wilson,+1555666777,sarah.wilson@example.com,Global Corp
David Brown,+1888999000,david.brown@example.com,Startup Inc`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template_single_agent.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadMultiAgentTemplate = () => {
    const csvContent = `Name,Phone Number,Email,Company,Agent ID
John Doe,+1234567890,john.doe@example.com,ABC Company,1
Jane Smith,+0987654321,jane.smith@example.com,XYZ Corp,2
Mike Johnson,+1122334455,mike.johnson@example.com,Tech Solutions,3
Sarah Wilson,+1555666777,sarah.wilson@example.com,Global Corp,4
David Brown,+1888999000,david.brown@example.com,Startup Inc,5`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template_with_agent_ids.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Upload Contacts CSV
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Upload contacts from a CSV file with agent assignment
          </p>
        </div>

        {/* Upload Section */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="space-y-6">
            {/* Agent Assignment Options */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Agent Assignment
              </h3>
              
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={!useCsvAgentIds}
                    onChange={() => setUseCsvAgentIds(false)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Assign all contacts to a specific agent
                  </span>
                </label>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={useCsvAgentIds}
                    onChange={() => setUseCsvAgentIds(true)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Use agent IDs from CSV file (each contact can have different agent)
                  </span>
                </label>
              </div>

              {/* Agent Autocomplete */}
              {!useCsvAgentIds && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Agent
                  </label>
                  <Autocomplete<User>
                    placeholder="Search for an agent..."
                    value={agentSearchValue}
                    onChange={(value) => setAgentSearchValue(value)}
                    onSelect={(agent) => setSelectedAgent(agent)}
                    searchFunction={searchAgents}
                    debounceMs={300}
                    displayKey="name"
                    subDisplayKey="email"
                  />
                  {selectedAgent && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-700">
                      <span className="text-sm text-blue-800 dark:text-blue-200">
                        Selected: {selectedAgent.name} ({selectedAgent.email})
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select CSV File
              </label>
              <div className="flex items-center space-x-4">
                <input
                  id="csv-file"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 dark:text-gray-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    dark:file:bg-blue-900 dark:file:text-blue-300"
                  disabled={uploading}
                />
                {file && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                )}
              </div>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <Button
                onClick={handleUpload}
                disabled={!file || uploading || (!useCsvAgentIds && !selectedAgent)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {uploading ? 'Uploading...' : 'Upload Contacts'}
              </Button>
            </div>
          </div>
        </div>

        {/* CSV Templates Section */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            CSV Templates
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Single Agent Template */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                Single Agent Template
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Use this template when you want to assign all contacts to one specific agent.
              </p>
              <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 mb-3">
                <code className="text-xs text-gray-700 dark:text-gray-300">
                  Name, Phone Number, Email, Company
                </code>
              </div>
              <Button
                onClick={downloadSingleAgentTemplate}
                variant="outline"
                size="sm"
                className="w-full"
              >
                Download Single Agent Template
              </Button>
            </div>

            {/* Multi-Agent Template */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                Multi-Agent Template
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Use this template when you want to assign different contacts to different agents.
              </p>
              <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 mb-3">
                <code className="text-xs text-gray-700 dark:text-gray-300">
                  Name, Phone Number, Email, Company, Agent ID
                </code>
              </div>
              <Button
                onClick={downloadMultiAgentTemplate}
                variant="outline"
                size="sm"
                className="w-full"
              >
                Download Multi-Agent Template
              </Button>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-4">
            Instructions
          </h3>
          <div className="space-y-3 text-sm text-blue-800 dark:text-blue-200">
            <div className="flex items-start space-x-2">
              <span className="text-blue-600 dark:text-blue-400">1.</span>
              <span>Choose agent assignment method (single agent or CSV-based)</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600 dark:text-blue-400">2.</span>
              <span>Download the appropriate CSV template from the templates section above</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600 dark:text-blue-400">3.</span>
              <span>Fill in your contact data following the template format</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600 dark:text-blue-400">4.</span>
              <span>Save the file as CSV format</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-blue-600 dark:text-blue-400">5.</span>
              <span>Upload the file using the form above</span>
            </div>
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-medium text-yellow-900 dark:text-yellow-100 mb-4">
            Requirements
          </h3>
          <ul className="space-y-2 text-sm text-yellow-800 dark:text-yellow-200">
            <li>• File must be in CSV format</li>
            <li>• Maximum file size: 10MB</li>
            <li>• Phone numbers should include country code (e.g., +1234567890)</li>
            <li>• Email addresses should be valid format</li>
            <li>• First row should contain column headers</li>
            {useCsvAgentIds && (
              <li>• Agent ID must be a valid user ID from the system</li>
            )}
          </ul>
        </div>
      </div>
    </MainLayout>
  );
};

export default UploadContactsCsv; 